import { useEffect, useState } from "react";
import { fetchBrands, hubSetBrandClosed } from "@/lib/api";
import { brandOpenState } from "@/lib/brandAvailability";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });
export const isManuallyClosed = (b) => !!b.temporarily_closed && (!b.reopen_at || new Date(b.reopen_at) > new Date());

const plusMinutes = (m) => new Date(Date.now() + m * 60000).toISOString();
const todayAt = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    if (d <= new Date()) d.setDate(d.getDate() + 1);
    return d.toISOString();
};

const ReopenMenu = ({ brand, onPick, onCancel }) => {
    const [time, setTime] = useState("");
    const opt = "w-full text-left font-mono text-[11px] uppercase tracking-[0.14em] px-3 py-2.5 hover:bg-[var(--surface-elevated)] hover:text-gold";
    return (
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCancel(); }}>
            <div className="w-full max-w-xs bg-[var(--surface)] border border-[#ff5246] p-4" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} data-testid="reopen-menu">
                <p className="eyebrow mb-1 text-[#ff5246]">// Close {brand.name}</p>
                <p className="text-[12px] text-muted mb-3">All new orders are blocked. Reopen automatically:</p>
                <button className={opt} onClick={() => onPick(plusMinutes(30))} data-testid="reopen-30">In 30 minutes</button>
                <button className={opt} onClick={() => onPick(plusMinutes(60))} data-testid="reopen-60">In 1 hour</button>
                <button className={opt} onClick={() => onPick(plusMinutes(120))} data-testid="reopen-120">In 2 hours</button>
                <div className="flex gap-2 px-3 py-2">
                    <input type="time" className="input flex-1 text-[12px]" value={time} onChange={(e) => setTime(e.target.value)} data-testid="reopen-time-input" />
                    <button className="btn-secondary text-[11px] px-3" disabled={!time} onClick={() => onPick(todayAt(time))} data-testid="reopen-at-time">Set</button>
                </div>
                <button className={opt} onClick={() => onPick(null)} data-testid="reopen-manual">Until I reopen it</button>
                <button className="w-full font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2 py-2" onClick={onCancel} data-testid="reopen-cancel">Cancel</button>
            </div>
        </div>
    );
};

// One-tap OPEN / CLOSED pill. `onToggle(nextClosed, reopenAtIso|null)` must persist and resolve.
export const BrandOpenToggle = ({ brand, onToggle, size = "sm" }) => {
    const [busy, setBusy] = useState(false);
    const [picking, setPicking] = useState(false);
    const closed = isManuallyClosed(brand);
    const hours = brandOpenState({ ...brand, temporarily_closed: false });
    const run = async (nextClosed, reopenAt = null) => {
        setPicking(false);
        setBusy(true);
        try { await onToggle(nextClosed, reopenAt); }
        catch (ex) { alert(ex.response?.data?.detail || "Could not update brand"); }
        finally { setBusy(false); }
    };
    const click = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (closed) run(false);
        else setPicking(true);
    };
    const label = closed
        ? (brand.reopen_at ? `● Closed · ${fmtTime(brand.reopen_at)}` : "● Closed")
        : hours.available ? "● Open" : "● Open (hrs)";
    return (
        <>
            <button
                type="button"
                onClick={click}
                disabled={busy}
                className={`font-mono font-bold uppercase tracking-[0.16em] border transition-colors disabled:opacity-50 ${size === "lg" ? "text-[12px] px-4 py-2.5 min-w-[130px]" : "text-[10px] px-2.5 py-1.5"} ${closed ? "bg-[#ff5246] border-[#ff5246] text-white" : "bg-[#1f9d55] border-[#1f9d55] text-white"}`}
                title={closed ? "Tap to reopen now" : hours.available ? "Open — tap to close" : `Outside hours (${hours.nextOpenLabel}) — tap to close`}
                data-testid={`brand-open-toggle-${brand.slug}`}
            >
                {busy ? "…" : label}
            </button>
            {picking && <ReopenMenu brand={brand} onPick={(iso) => run(true, iso)} onCancel={() => setPicking(false)} />}
        </>
    );
};

export const HubBrandsPanel = ({ onClose, onChanged }) => {
    const [brands, setBrands] = useState(null);
    useEffect(() => { fetchBrands().then(setBrands).catch(() => setBrands([])); }, []);
    const toggle = (b) => async (closed, reopenAt) => {
        const r = await hubSetBrandClosed(b.id, closed, reopenAt);
        setBrands((cur) => cur.map((x) => (x.id === b.id ? { ...x, temporarily_closed: r.temporarily_closed, reopen_at: r.reopen_at } : x)));
        onChanged?.();
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="hub-brands-panel">
            <div className="w-full max-w-xl max-h-[85vh] overflow-y-auto bg-[var(--surface)] border border-[var(--border)] p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <p className="eyebrow">// Brands</p>
                        <h2 className="font-display text-[28px] leading-none mt-1">Open / Closed</h2>
                    </div>
                    <button onClick={onClose} className="btn-ghost" data-testid="hub-brands-close">Done</button>
                </div>
                {!brands ? (
                    <p className="text-muted text-sm">Loading…</p>
                ) : (
                    <div className="divide-y divide-[var(--border)]">
                        {brands.map((b) => {
                            const h = brandOpenState({ ...b, temporarily_closed: false });
                            const manual = isManuallyClosed(b);
                            return (
                                <div key={b.id} className="flex items-center gap-3 py-3" data-testid={`hub-brand-row-${b.slug}`}>
                                    <div className="logo-box shrink-0" style={{ width: 36, height: 36, fontSize: 13 }}>{b.token}</div>
                                    <div className="min-w-0 flex-1">
                                        <div className="font-display text-[17px] truncate" style={{ color: b.accent }}>{b.name}</div>
                                        <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
                                            {manual ? (b.reopen_at ? `Closed — reopens ${fmtTime(b.reopen_at)}` : "Closed manually — orders blocked") : h.available ? "Within opening hours" : h.nextOpenLabel}
                                        </div>
                                    </div>
                                    <BrandOpenToggle brand={b} onToggle={toggle(b)} size="lg" />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

// Hub banner so a manually closed brand is never forgotten.
export const HubClosedBrandsAlert = ({ onManage, refreshKey }) => {
    const [closed, setClosed] = useState([]);
    useEffect(() => {
        const load = () => fetchBrands().then((bs) => setClosed(bs.filter(isManuallyClosed))).catch(() => {});
        load();
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [refreshKey]);
    if (!closed.length) return null;
    return (
        <button
            onClick={onManage}
            className="w-full bg-[#ff5246] text-white font-mono text-[11px] uppercase tracking-[0.16em] px-5 py-2.5 text-left flex items-center justify-between gap-3"
            data-testid="hub-closed-brands-alert"
        >
            <span className="truncate">
                ⚠ {closed.length} brand{closed.length > 1 ? "s" : ""} closed:{" "}
                {closed.map((b) => `${b.name}${b.reopen_at ? ` (back ${fmtTime(b.reopen_at)})` : ""}`).join(" · ")}
            </span>
            <span className="shrink-0 font-bold">Manage →</span>
        </button>
    );
};
